import type { CefrLevel } from "@rmnlng/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./api";
import { useMe } from "./auth";

// ─── Types ────────────────────────────────────────────────────
//
// Mirror of the backend DTOs in `apps/api/src/scenarios/scenarios.service.ts`.
// Keep these in sync — when fields change on the server side, update
// here too. We don't import from `@rmnlng/shared` because scenario
// types live in the API package; sharing them would mean lifting them
// into shared, which is more churn than it's worth at this stage.

export type ScenarioStatus =
  | "LOCKED"
  | "AVAILABLE"
  | "IN_PROGRESS"
  | "READY_FOR_CHECKPOINT"
  | "COMPLETED"
  | "NEEDS_REVIEW";

export type ScenarioProgress = {
  status: ScenarioStatus;
  startedAt: string | null;
  completedAt: string | null;
  stepsCompleted: number;
  checkpointScore: number | null;
  checkpointAttempts: number;
  weakRules: string[];
};

export type ScenarioSummary = {
  slug: string;
  title: string;
  level: CefrLevel;
  stage: number;
  capstone: boolean;
  promise: string;
  estimatedMinutes: number;
  wordCount: number;
  ruleCount: number;
  constructionCount: number;
  prerequisiteSlugs: string[];
  tags: string[];
  // Present only when fetched with `?withProgress=true`.
  progress?: ScenarioProgress | null;
};

export type ScenarioWord = {
  lemma: string;
  translation: string | null;
  pos: string | null;
  gender: string | null;
  pronunciation: string | null;
};

export type ScenarioRule = {
  slug: string;
  title: string | null;
  summary: string | null;
  body: string | null;
  // Inline MCQ practice surfaced inside the session's RULE step.
  // Empty = the rule is text-only and the step shows just "Понятно".
  examples: { prompt: string; options: string[]; answer: string }[];
};

export type ScenarioConstruction = {
  // Synthetic id, exposed so the LINE step can grade against
  // `ChunkProgress` without a separate slug-resolution round-trip.
  id: string;
  ro: string;
  ru: string;
  position: number;
};

export type ScenarioDetail = ScenarioSummary & {
  words: ScenarioWord[];
  rules: ScenarioRule[];
  constructions: ScenarioConstruction[];
  checkpointQuizSlug: string | null;
  completionCriteria: {
    sessionStepsCompleted: "all" | number;
    checkpointPassed: boolean;
  };
};

export type ScenarioSession = ScenarioDetail & {
  status: ScenarioStatus;
  // Indices the user has completed. Local step list (regenerated by
  // `generateScenarioSteps`) is matched against this to find resume
  // index.
  completedStepIndices: number[];
};

// ─── Checkpoint (Phase 6) ─────────────────────────────────────

export type CheckpointItem = {
  slug: string;
  kind: string;
  prompt: string;
  options: string[];
  hints: string[];
  level: string;
  sourceSentence: string | null;
  sourceTranslation: string | null;
  translation: string | null;
};

export type CheckpointDto = {
  scenarioSlug: string;
  totalItems: number;
  passingScore: number;
  items: CheckpointItem[];
};

export type CheckpointOutcome = "PASSED" | "BORDERLINE" | "FAILED";

export type CheckpointPerItemResult = {
  itemSlug: string;
  correct: boolean;
  expected: string;
  given: string;
  explanation: string | null;
  rulesTested: string[];
};

export type CheckpointResult = {
  score: number;
  correctCount: number;
  totalItems: number;
  outcome: CheckpointOutcome;
  weakRules: string[];
  perItem: CheckpointPerItemResult[];
};

// ─── Tree + Stall (Phase 7) ───────────────────────────────────

export type ScenarioTreeItem = {
  slug: string;
  title: string;
  level: CefrLevel;
  status: ScenarioStatus;
  capstone: boolean;
  prerequisiteSlugs: string[];
};
export type ScenarioTreeStage = { stage: number; items: ScenarioTreeItem[] };
export type ScenarioTree = { stages: ScenarioTreeStage[] };

export type StallDomain = {
  domain: string;
  level: CefrLevel;
  have: number;
  need: number;
  unlocksScenarios: { slug: string; title: string }[];
};
export type StallRecommendations = { domains: StallDomain[] };

export const scenarioKeys = {
  all: ["scenarios"] as const,
  list: (level: CefrLevel | undefined, withProgress: boolean) =>
    ["scenarios", "list", { level, withProgress }] as const,
  detail: (slug: string, withProgress: boolean) => ["scenarios", "detail", slug, { withProgress }] as const,
  session: (slug: string) => ["scenarios", "session", slug] as const,
  checkpoint: (slug: string) => ["scenarios", "checkpoint", slug] as const,
  tree: () => ["scenarios", "tree"] as const,
  stall: () => ["scenarios", "stall"] as const,
};

// ─── List + detail ────────────────────────────────────────────

/**
 * List scenarios. Fetches with `withProgress=true` automatically when
 * the user is in SCENARIOS mode — the journal needs status badges to
 * render the right card variant. Other modes can still call this hook
 * (e.g. an admin browsing scenarios) without progress fields.
 */
export function useScenarios(opts: { level?: CefrLevel; withProgress?: boolean } = { withProgress: true }) {
  const { data: user } = useMe();
  return useQuery({
    queryKey: scenarioKeys.list(opts.level, opts.withProgress as boolean),
    queryFn: () => {
      const params = new URLSearchParams();
      if (opts.level) params.set("level", opts.level);
      if (opts.withProgress) params.set("withProgress", "true");
      return apiFetch<ScenarioSummary[]>(`/scenarios${params.toString() ? `?${params}` : ""}`);
    },
    enabled: !!user,
    staleTime: 30_000,
  });
}

export function useScenario(slug: string | undefined) {
  const { data: user } = useMe();
  const withProgress = user?.studyMode === "SCENARIOS";
  return useQuery({
    queryKey: scenarioKeys.detail(slug ?? "", withProgress),
    queryFn: () => {
      const params = new URLSearchParams();
      if (withProgress) params.set("withProgress", "true");
      return apiFetch<ScenarioDetail>(`/scenarios/${slug}${params.toString() ? `?${params}` : ""}`);
    },
    enabled: !!slug && !!user,
  });
}

// ─── Mutations ────────────────────────────────────────────────
//
// Phase 4 wires `useStartScenario` (the only mutation needed to put
// users into IN_PROGRESS for read-only browsing). Step completion +
// checkpoint submit are Phase 5/6.

export function useStartScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { slug: string }) =>
      apiFetch(`/scenarios/${args.slug}/start`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    // Invalidate every list/detail query so the journal + the just-
    // started scenario both pick up the new status.
    onSuccess: () => qc.invalidateQueries({ queryKey: scenarioKeys.all }),
  });
}

// ─── Session (Phase 5) ────────────────────────────────────────

/**
 * Fetch session resume state. 404 → user hasn't pressed Start yet;
 * the SessionController surfaces that as a redirect back to Detail.
 *
 * `staleTime: 0` because session state advances on every step
 * completion; we re-fetch on focus so a multi-tab user sees coherent
 * progress.
 */
export function useScenarioSession(slug: string | undefined) {
  return useQuery({
    queryKey: scenarioKeys.session(slug ?? ""),
    queryFn: () => apiFetch<ScenarioSession>(`/scenarios/${slug}/session`),
    enabled: !!slug,
    staleTime: 0,
  });
}

export function useCompleteStep(slug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      stepIndex: number;
      stepKind: "WORD" | "RULE" | "CHUNK";
      totalSteps: number;
      payload?: Record<string, unknown>;
    }) =>
      apiFetch(`/scenarios/${slug}/steps/${args.stepIndex}/complete`, {
        method: "POST",
        body: JSON.stringify({
          stepKind: args.stepKind,
          totalSteps: args.totalSteps,
          payload: args.payload,
        }),
      }),
    // The session detail and the journal-side detail may both be open;
    // invalidate everything scenario-shaped so progress propagates.
    onSuccess: () => qc.invalidateQueries({ queryKey: scenarioKeys.all }),
  });
}

export function useGradeChunk(slug: string | undefined) {
  return useMutation({
    mutationFn: (args: { chunkId: string; correct: boolean }) =>
      apiFetch(`/scenarios/${slug}/chunks/${args.chunkId}/grade`, {
        method: "POST",
        body: JSON.stringify({ correct: args.correct }),
      }),
  });
}

// ─── Checkpoint hooks (Phase 6) ───────────────────────────────

export function useCheckpoint(slug: string | undefined) {
  return useQuery({
    queryKey: scenarioKeys.checkpoint(slug ?? ""),
    queryFn: () => apiFetch<CheckpointDto>(`/scenarios/${slug}/checkpoint`),
    enabled: !!slug,
    // Items are stable per scenario; long stale-time avoids refetching
    // mid-attempt when react-query window-focuses.
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useSubmitCheckpoint(slug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (answers: { itemSlug: string; given: string }[]) =>
      apiFetch<CheckpointResult>(`/scenarios/${slug}/checkpoint/submit`, {
        method: "POST",
        body: JSON.stringify({ answers }),
      }),
    // Status changes (READY_FOR_CHECKPOINT → COMPLETED / IN_PROGRESS),
    // so blow away every scenario-shaped cache so journal + detail
    // pick up the new state.
    onSuccess: () => qc.invalidateQueries({ queryKey: scenarioKeys.all }),
  });
}

export function useAcceptBorderline(slug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; outcome?: CheckpointOutcome }>(`/scenarios/${slug}/checkpoint/accept-borderline`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: scenarioKeys.all }),
  });
}

// ─── Tree + Stall hooks (Phase 7) ─────────────────────────────

export function useScenarioTree() {
  const { data: user } = useMe();
  return useQuery({
    queryKey: scenarioKeys.tree(),
    queryFn: () => apiFetch<ScenarioTree>(`/scenarios/tree`),
    enabled: !!user,
    staleTime: 30_000,
  });
}

export function useStallRecommendations(opts: { enabled?: boolean } = {}) {
  const { data: user } = useMe();
  return useQuery({
    queryKey: scenarioKeys.stall(),
    queryFn: () => apiFetch<StallRecommendations>(`/scenarios/stall-recommendations`),
    enabled: !!user && (opts.enabled ?? true),
    staleTime: 60_000,
  });
}

// "Stall" condition — the journal has nothing actionable. With the
// new prerequisite-chain lock model this only fires if EVERY scenario
// is LOCKED (no foundational entry-point), which means the seed data
// is misconfigured. The UI still renders a stall screen as a safety
// net, but in practice users always land on at least one AVAILABLE.
export function isStallCondition(scenarios: ScenarioSummary[] | undefined): boolean {
  if (!scenarios || scenarios.length === 0) return false;
  const has = (status: ScenarioStatus) => scenarios.some((s) => s.progress?.status === status);
  if (has("IN_PROGRESS") || has("READY_FOR_CHECKPOINT") || has("AVAILABLE")) return false;
  return has("LOCKED");
}

// ─── UI helpers ────────────────────────────────────────────────
//
// Small derivations the UI needs in multiple places. Putting them
// here so the page components stay focused on layout.

export function isLocked(s: ScenarioSummary | undefined): boolean {
  return s?.progress?.status === "LOCKED";
}

export function isStartable(s: ScenarioSummary | undefined): boolean {
  return s?.progress?.status === "AVAILABLE";
}

// "Я могу: 14 / 60 умений A1" — the journal's hero counter.
// Counts COMPLETED + NEEDS_REVIEW as "I can"; everything else is
// in-flight or not-started.
export function countMastered(scenarios: ScenarioSummary[] | undefined): number {
  if (!scenarios) return 0;
  return scenarios.filter((s) => s.progress?.status === "COMPLETED" || s.progress?.status === "NEEDS_REVIEW").length;
}
